import { AdminList, AdminDataGrid, GridColumn } from './shared';
/**
 * Content Moderation Log List Component
 * Displays paginated list of Llama Guard safety check results
 */

import {
  DateField,
  SearchInput,
  SelectInput,
  useRecordContext,
  FunctionField,
} from 'react-admin';
import { Chip, Tooltip, Typography, Box } from '@mui/material';
import { semantic } from '@rekindle/shared/theme';
import { ListActions } from './shared';

/**
 * Action color mapping
 */
const ACTION_COLORS: Record<string, string> = {
  allowed: semantic.success,
  blocked: semantic.error,
  crisis_intervention: semantic.warning,
  regenerated: semantic.warning,
  default: semantic.neutral,
};

/**
 * Custom field to display action with color coding
 */
function ActionField() {
  const record = useRecordContext();
  if (!record?.actionTaken) return null;

  const color = ACTION_COLORS[record.actionTaken] || ACTION_COLORS.default;

  return (
    <Chip
      label={record.actionTaken.replace(/_/g, ' ').toUpperCase()}
      size="small"
      sx={{
        backgroundColor: color,
        color: 'white',
        fontWeight: 500,
        fontSize: '0.7rem',
      }}
    />
  );
}

/**
 * Custom field to display direction
 */
function DirectionField() {
  const record = useRecordContext();
  if (!record?.direction) return null;

  const isInput = record.direction === 'input';

  return (
    <Chip
      label={isInput ? 'USER' : 'AI'}
      size="small"
      variant="outlined"
      color={isInput ? 'primary' : 'secondary'}
    />
  );
}

/**
 * Safety status field
 */
function SafetyField() {
  const record = useRecordContext();
  if (record?.isSafe === undefined) return null;

  return (
    <Chip
      label={record.isSafe ? 'SAFE' : 'UNSAFE'}
      size="small"
      sx={{
        backgroundColor: record.isSafe ? semantic.success : semantic.error,
        color: 'white',
        fontWeight: 600,
        fontSize: '0.7rem',
      }}
    />
  );
}

/**
 * Flagged categories field
 */
function FlaggedCategoriesField() {
  const record = useRecordContext();
  if (!record?.flaggedCategories || !Array.isArray(record.flaggedCategories) || record.flaggedCategories.length === 0) {
    return <Typography variant="body2" color="textSecondary">-</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {record.flaggedCategories.map((flag: { category: string }, i: number) => (
        <Chip
          key={i}
          label={flag.category?.replace(/_/g, ' ')}
          size="small"
          variant="outlined"
          color="error"
          sx={{ fontSize: '0.65rem' }}
        />
      ))}
    </Box>
  );
}

/**
 * Content preview with tooltip for full text
 */
function ContentPreviewField() {
  const record = useRecordContext();
  if (!record?.content) return <Typography variant="body2" color="textSecondary">-</Typography>;

  const truncated = record.content.length > 80
    ? record.content.substring(0, 80) + '...'
    : record.content;

  return (
    <Tooltip title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap', maxWidth: 400 }}>{record.content}</pre>}>
      <Typography
        variant="body2"
        sx={{
          maxWidth: 250,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: 'help',
          fontSize: '0.8rem',
        }}
      >
        {truncated}
      </Typography>
    </Tooltip>
  );
}

/**
 * User display field
 */
function UserField() {
  const record = useRecordContext();
  if (!record?.user) {
    return (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
        {record?.userId?.substring(0, 8)}...
      </Typography>
    );
  }

  const user = record.user as { email?: string; name?: string };
  return (
    <Tooltip title={user.email || record.userId}>
      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
        {user.name || user.email || record.userId?.substring(0, 8)}
      </Typography>
    </Tooltip>
  );
}

/**
 * Filters for moderation log list
 */
const moderationFilters = [
  <SearchInput key="q" source="q" alwaysOn placeholder="Search content..." />,
  <SelectInput
    key="isSafe"
    source="isSafe"
    label="Safety"
    choices={[
      { id: 'true', name: 'Safe' },
      { id: 'false', name: 'Unsafe' },
    ]}
  />,
  <SelectInput
    key="direction"
    source="direction"
    label="Direction"
    choices={[
      { id: 'input', name: 'User Input' },
      { id: 'output', name: 'AI Output' },
    ]}
  />,
  <SelectInput
    key="actionTaken"
    source="actionTaken"
    label="Action"
    choices={[
      { id: 'allowed', name: 'Allowed' },
      { id: 'blocked', name: 'Blocked' },
      { id: 'crisis_intervention', name: 'Crisis Intervention' },
      { id: 'regenerated', name: 'Regenerated' },
    ]}
  />,
];

export function ModerationLogList() {
  return (
    <AdminList
      filters={moderationFilters}
      actions={<ListActions />}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={50}
    >
      <AdminDataGrid bulkActionButtons={false}>
        <DateField source="createdAt" label="Date" showTime />
        <GridColumn label="Safety" source="isSafe"><SafetyField /></GridColumn>
        <GridColumn label="Direction" source="direction"><DirectionField /></GridColumn>
        <GridColumn label="Action" source="actionTaken"><ActionField /></GridColumn>
        <GridColumn label="Flagged categories" sortable={false}><FlaggedCategoriesField /></GridColumn>
        <GridColumn label="Content" sortable={false}><ContentPreviewField /></GridColumn>
        <GridColumn label="User" sortable={false}><UserField /></GridColumn>
        <FunctionField
          label="Latency"
          render={(record: { latencyMs?: number }) =>
            record?.latencyMs ? `${record.latencyMs}ms` : '-'
          }
        />
      </AdminDataGrid>
    </AdminList>
  );
}
